var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './button.styles';
import { handleFilePreview } from '../helpers/pFrontend';
let PButton = class PButton extends LitElement {
    constructor() {
        super(...arguments);
        this.color = 'primary';
        this.size = 'medium';
        this.caret = false;
        this.icon = false;
        this.disabled = false;
        this.loading = false;
        this.download = false;
        this.successAnimating = false;
        /**
         * Handle click event
         * @param {Event} e - Event
         * @returns {void}
         */
        this.handleClick = async (e) => {
            if (this.disabled || this.loading) {
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            this.loading = true;
            this.handleTargetDialog();
            await handleFilePreview(this.fileId);
            this.dispatchEvent(new CustomEvent('p-click', { bubbles: true, composed: true }));
            this.handleBlur();
            this.loading = false;
        };
    }
    static { this.styles = styles; }
    /**
     * Handle blur event
     * @returns {void}
     */
    handleBlur() {
        this.dispatchEvent(new CustomEvent('p-blur', { bubbles: true, composed: true }));
    }
    /**
     * Handle focus event
     * @returns {void}
     */
    handleFocus() {
        this.dispatchEvent(new CustomEvent('p-focus', { bubbles: true, composed: true }));
    }
    /**
     * Set focus on button
     * @returns {void}
     */
    setFocus() {
        this.button.focus();
    }
    handleTargetDialog() {
        if (!this.targetDialog)
            return;
        document.querySelectorAll(this.targetDialog).forEach((dialog) => {
            dialog.open();
        });
    }
    /**
     * Show button success animation
     * @returns {void}
     */
    success() {
        this.loading = false;
        this.successAnimating = true;
        // Add success class
        setTimeout(() => {
            this.button.classList.add('success');
        }, 10);
        setTimeout(() => {
            this.button.classList.remove('success');
            this.successAnimating = false;
        }, 1400);
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const isLink = this.href ? true : false;
        const classes = {
            [`${this.color}`]: true,
            [`${this.size}`]: true,
            icon: this.icon,
            loading: this.loading,
            'button--caret': this.caret,
            active: this.pressed === 'true'
        };
        const arrowSVG = this.color == 'arrow'
            ? html ` <svg aria-hidden="true" width="15" height="11" viewBox="0 0 14 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg" > <path fill-rule="evenodd" fill="currentColor" clip-rule="evenodd" d="M0 5.00014C0 4.86753 0.0526784 4.74036 0.146447 4.64659C0.240215 4.55282 0.367392 4.50014 0.5 4.50014H12.293L9.146 1.35414C9.05211 1.26026 8.99937 1.13292 8.99937 1.00014C8.99937 0.867368 9.05211 0.74003 9.146 0.646143C9.23989 0.552257 9.36722 0.499512 9.5 0.499512C9.63278 0.499512 9.76011 0.552257 9.854 0.646143L13.854 4.64614C13.9006 4.69259 13.9375 4.74776 13.9627 4.80851C13.9879 4.86926 14.0009 4.93438 14.0009 5.00014C14.0009 5.06591 13.9879 5.13103 13.9627 5.19178C13.9375 5.25252 13.9006 5.3077 13.854 5.35414L9.854 9.35414C9.76011 9.44803 9.63278 9.50077 9.5 9.50077C9.36722 9.50077 9.23989 9.44803 9.146 9.35414C9.05211 9.26026 8.99937 9.13292 8.99937 9.00014C8.99937 8.86737 9.05211 8.74003 9.146 8.64614L12.293 5.50014H0.5C0.367392 5.50014 0.240215 5.44746 0.146447 5.3537C0.0526784 5.25993 0 5.13275 0 5.00014Z" /> </svg>`
            : '';
        const caretSVG = this.caret
            ? html ` <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg>`
            : '';
        return isLink
            ? html ` <a ?disabled=${this.disabled} class="button${classMap(classes)}" tabindex=${this.disabled ? '-1' : '0'} @blur=${this.handleBlur} @focus=${this.handleFocus} @click=${this.handleClick} href=${ifDefined(this.href)} target=${ifDefined(this.target)} aria-label=${ifDefined(this.label)} aria-expanded=${ifDefined(this.expanded)} aria-controls=${ifDefined(this.controls)} aria-pressed=${ifDefined(this.pressed)} ?download=${this.download} > <slot name="prefix"></slot> <span class="slot-wrapper"> <slot></slot>${caretSVG}${arrowSVG} </span> <slot name="suffix"></slot> </a> `
            : html ` <button ?disabled=${this.disabled} class="button${classMap(classes)}" tabindex=${this.disabled ? '-1' : '0'} @blur=${this.handleBlur} @focus=${this.handleFocus} @click=${this.handleClick} aria-label=${ifDefined(this.label)} aria-expanded=${ifDefined(this.expanded)} aria-controls=${ifDefined(this.controls)} aria-pressed=${ifDefined(this.pressed)} ?download=${this.download} > <slot name="prefix"></slot> <span class="slot-wrapper"> <slot></slot>${caretSVG}${arrowSVG} ${this.successAnimating ? html `
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      class="success-icon"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z"
                      />
                    </svg>
                  `
                : ''}
            </span>
            <slot name="suffix"></slot>
          </button>
        `;
    }
};
__decorate([
    property({ type: String })
], PButton.prototype, "color", void 0);
__decorate([
    property({ type: String })
], PButton.prototype, "size", void 0);
__decorate([
    property({ type: Boolean })
], PButton.prototype, "caret", void 0);
__decorate([
    property({ type: Boolean })
], PButton.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PButton.prototype, "loading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PButton.prototype, "download", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "href", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "target", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "expanded", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "controls", void 0);
__decorate([
    property({ type: String, reflect: true })
], PButton.prototype, "pressed", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'target-dialog' })
], PButton.prototype, "targetDialog", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'file-id' })
], PButton.prototype, "fileId", void 0);
__decorate([
    state()
], PButton.prototype, "successAnimating", void 0);
__decorate([
    query('.button')
], PButton.prototype, "button", void 0);
PButton = __decorate([
    customElement('p-button')
    /**
     * Button component
     *
     * @class PButton
     * @typedef {PButton}
     * @extends {LitElement}
     */
], PButton);
export { PButton };
